import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCompetenciasComDado } from '../../../api/queries/chamados'
import { Carregando, EstadoVazio } from '../../../components/Estados'
import { IconeIndicador } from '../../../components/Icones'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import { BotaoDeCarga } from '../importacao/BotaoDeCarga'
import { HistoricoDeCargas } from '../importacao/HistoricoDeCargas'
import { SituacaoDaSincronizacao } from '../importacao/SituacaoDaSincronizacao'
import { CalendarioDaApuracao } from './CalendarioDaApuracao'
import { FechamentoDaCompetencia } from './FechamentoDaCompetencia'

/**
 * O fechamento do mês: a tela que o fiscal usa para assinar o termo de recebimento.
 *
 * A competência vive na URL, e não em `useState`, pelo mesmo motivo dos filtros da listagem: o
 * fechamento de maio é um endereço — `/fiscalizacao?competencia=2026-05` — que vai por e-mail para
 * quem vai conferir, e recarregar a página não pode devolver a pessoa a outro mês. Sem competência
 * na URL, vale a mais recente que tem dado, que é o que quem chega pelo menu quer ver.
 *
 * @returns a tela de fechamento
 */
export function PaginaFechamento() {
  const [parametros, definirParametros] = useSearchParams()
  const { data: competencias, isPending: carregandoCompetencias } = useCompetenciasComDado()

  const escolhida = parametros.get('competencia') ?? ''
  // A mais recente é a primeira da lista: o servidor devolve em ordem decrescente.
  const competencia = escolhida || competencias?.[0]?.competencia || ''

  const escolher = useCallback(
    (valor: string) => {
      const proximos = new URLSearchParams(parametros)
      proximos.set('competencia', valor)
      definirParametros(proximos)
    },
    [parametros, definirParametros],
  )

  // O calendário vem depois dos números e antes das cargas: ele explica COMO os prazos foram
  // contados, e é a primeira coisa que a contratada questiona quando discorda de um SLA. A
  // atualização automática mora junto do histórico de cargas: as rodadas aparecem lá, com a origem
  // "e-Niterói (API)", como qualquer carga. Não dependem do mês escolhido — por isso aparecem também
  // quando ainda não há mês nenhum para apurar.
  const fontes = (
    <div className="space-y-5">
      <CalendarioDaApuracao />
      <SituacaoDaSincronizacao />
      <HistoricoDeCargas />
    </div>
  )
  const temMes = Boolean(competencias && competencias.length > 0)

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{TERMOS.fechamentoDoMes.rotulo}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.fiscalizacao.ajuda}</p>
        </div>
        <BotaoDeCarga aparencia="secundario" />
      </header>

      {carregandoCompetencias && <Carregando />}

      {competencias && competencias.length === 0 && (
        <EstadoVazio
          icone={<IconeIndicador className="h-9 w-9" />}
          titulo={TEXTOS.fiscalizacao.semCompetencias.titulo}
          ajuda={TEXTOS.fiscalizacao.semCompetencias.ajuda}
          acao={<BotaoDeCarga />}
        />
      )}

      {competencias && temMes && (
        <FechamentoDaCompetencia
          competencias={competencias}
          competencia={competencia}
          aoEscolher={escolher}
          fontes={fontes}
        />
      )}

      {!temMes && fontes}
    </div>
  )
}
